import type {
    PublicMediaItem,
} from "@mrflark/spl_service_model";
import {
    LoadingSpinner,
} from "@mrflark/ui";
import {
    useEffect,
    useRef,
    useState,
} from "preact/compat";
import {
    Link,
    useParams,
    useSearchParams,
} from "react-router-dom";
import {
    splClient,
} from "../api/client.ts";
import {
    extractErrorMessage,
} from "../hooks/utils.ts";

export default function PermalinkPage() {
    const {
        itemId,
    } = useParams<{ itemId: string; }>();
    const [searchParams] = useSearchParams();
    const [item, setItem] = useState<PublicMediaItem | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const fetchItem = async () => {
            if (!itemId) {
                setError("No item ID provided");
                setIsLoading(false);
                return;
            }

            try {
                const params = new URLSearchParams(searchParams);
                const qs = params.toString();
                const data = await splClient.get<PublicMediaItem>(
                    `/api/public/media/${itemId}${qs ? `?${qs}` : ""}`,
                );
                setItem(data);
            } catch (err) {
                setError(extractErrorMessage(err, "Failed to load media"));
            } finally {
                setIsLoading(false);
            }
        };

        fetchItem();
    }, [itemId, searchParams]);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.volume = 0.85;
        }
    }, [item?.id]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-bg-primary flex items-center justify-center">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    if (error || !item) {
        return (
            <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
                <div className="text-center">
                    <p className="text-error text-lg mb-4">
                        {error || "Item not found"}
                    </p>
                    <Link
                        to="/"
                        className="text-accent hover:underline"
                    >
                        Go to library
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bg-primary flex flex-col items-center justify-center p-4">
            <div className="max-w-4xl w-full">
                <div className="flex items-center justify-center mb-4">
                    {item.media_type === "video"
                        ? (
                            <video
                                ref={videoRef}
                                key={item.id}
                                src={item.media_url}
                                controls
                                autoPlay
                                loop
                                playsInline
                                className="max-h-[70vh] max-w-full rounded-lg"
                            />
                        )
                        : (
                            <img
                                key={item.id}
                                src={item.media_url}
                                alt={`Media ${item.id}`}
                                className="max-h-[70vh] max-w-full rounded-lg object-contain"
                                loading="eager"
                            />
                        )}
                </div>

                <div className="flex items-center justify-center gap-6 text-sm text-text-secondary">
                    <span>
                        <span className="text-text-muted">ID:</span>{" "}
                        <span className="font-mono text-text-primary">
                            {item.id}
                        </span>
                    </span>
                    <span>
                        <span className="text-text-muted">Views:</span>{" "}
                        <span className="text-text-primary">{item.views}</span>
                    </span>
                    <span>
                        <span className="text-text-muted">Votes:</span>{" "}
                        <span className="text-accent font-medium">
                            {item.votes}
                        </span>
                    </span>
                </div>

                <div className="text-center mt-6">
                    <Link
                        to="/"
                        className="text-text-muted hover:text-text-primary text-sm"
                    >
                        Sign in to tag and vote
                    </Link>
                </div>
            </div>
        </div>
    );
}
